import {Box, Tab, Tabs, Typography} from '@mui/material';
import {AttendeeDetailsPageStyle} from './AttendeeDetails.styles';
import {useEffect, useMemo, useState} from 'react';
import {
  ATTENDEE_DETAILS_TABS,
  attendeeDetailsTabs,
  getAttendeeInitialValues,
} from '@/components/Organization/Attendees/AttendeeDetails/helpers';
import {Formik} from 'formik';
import AttendeeBidsDetailsTab from '@/components/Organization/Attendees/AttendeeDetails/AttendeeBidsDetailsTab';
import AttendeeContributionTabDetails from '@/components/Organization/Attendees/AttendeeDetails/AttendeeContributionTabDetails';
import FormContent from '@/components/Organization/Attendees/AttendeeDetails/FormContent';
import {
  AttendeeDetailsFormValues,
  initialValues,
  validationSchema,
} from '@/components/Organization/Attendees/AttendeeDetails/formConfiguration';
import {useLocation, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {clearAttendee, getAttendee, getAttendeeSelector} from '@/store/organization-attendees';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {OrganizationAttendeesService} from '@/utils/services';

const AttendeeDetailsPage = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const attendee = useAppSelector(getAttendeeSelector);

  const attendeeId = useParams().id;

  const [activeAttendeeTab, setActiveAttendeeTab] = useState<string>(location?.state?.activeTab || attendeeDetailsTabs[0]);
  const [isTabsVisible, setIsTabsVisible] = useState<boolean>(location?.state?.isTabsVisible ?? true);
  const changeAttendeeTab = (newTab: any) => () => setActiveAttendeeTab(newTab);

  const getAttendeeDetails = async (attendeeId?: number) => {
    if (attendeeId) {
      try {
        await dispatch(getAttendee(attendeeId)).unwrap();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    }
  };

  useEffect(() => {
    if (attendeeId) {
      getAttendeeDetails(+attendeeId);
    }
  }, [attendeeId]);

  useEffect(() => {
    return () => {
      dispatch(clearAttendee());
    };
  }, [dispatch]);

  const formikInitialValues = useMemo(() => {
    if (attendee) {
      const attendeeInitialValues = getAttendeeInitialValues(attendee);
      return attendeeInitialValues;
    }
    return initialValues;
  }, [attendee]);

  const handleSubmitClickHandler = async (values: AttendeeDetailsFormValues) => {
    try {
      const {id, ...reqBody} = values;
      await OrganizationAttendeesService.updateAttendee(id, reqBody);
      await getAttendeeDetails(attendee?.id);
      toast.success('Attendee information updated successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const attendeeTabData = useMemo(() => {
    switch (activeAttendeeTab) {
      case ATTENDEE_DETAILS_TABS.PROFILE:
        return (
          <>
            <Box sx={AttendeeDetailsPageStyle.attendeeDetails}>
              <Box sx={AttendeeDetailsPageStyle.block}>
                <Typography sx={AttendeeDetailsPageStyle.text}>Bid #</Typography>
                <Typography sx={AttendeeDetailsPageStyle.description}>{attendee?.id}</Typography>
              </Box>
              <Box sx={AttendeeDetailsPageStyle.block}>
                <Typography sx={AttendeeDetailsPageStyle.text}>Table #</Typography>
                <Typography sx={AttendeeDetailsPageStyle.description}>{attendee?.table?.number}</Typography>
              </Box>
              <Box sx={AttendeeDetailsPageStyle.block}>
                <Typography sx={AttendeeDetailsPageStyle.text}>Table name</Typography>
                <Typography sx={AttendeeDetailsPageStyle.description}>{attendee?.table?.name}</Typography>
              </Box>
              <Box sx={AttendeeDetailsPageStyle.block}>
                <Typography sx={AttendeeDetailsPageStyle.text}>Type of seat</Typography>
                <Typography sx={AttendeeDetailsPageStyle.description}>{attendee?.table?.tableInfo?.tier}</Typography>
              </Box>
              <Box sx={AttendeeDetailsPageStyle.block}>
                <Typography sx={AttendeeDetailsPageStyle.text}>Table purchaser</Typography>
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                  <Typography sx={AttendeeDetailsPageStyle.description}>
                    {attendee?.table?.purchaser
                      ? `${attendee.table?.purchaser?.firstName || ''} ${attendee.table?.purchaser?.lastName || ''}`.trim()
                      : ''}
                  </Typography>
                  <Typography sx={AttendeeDetailsPageStyle.text}>{attendee?.table?.purchaser?.email || ''}</Typography>
                </Box>
              </Box>
              <Box sx={AttendeeDetailsPageStyle.block}>
                <Typography sx={AttendeeDetailsPageStyle.text}>Contact phone</Typography>
                <Typography sx={AttendeeDetailsPageStyle.description}>
                  {attendee?.table?.purchaser?.phoneNumber ?? ''}
                </Typography>
              </Box>
            </Box>
            <Formik enableReinitialize initialValues={formikInitialValues} validationSchema={validationSchema} onSubmit={handleSubmitClickHandler}>
              <FormContent />
            </Formik>
          </>
        );

      case ATTENDEE_DETAILS_TABS.BIDS:
        return <AttendeeBidsDetailsTab />;

      case ATTENDEE_DETAILS_TABS.CONTRIBUTIONS:
        return <AttendeeContributionTabDetails />;

      default:
        break;
    }
  }, [activeAttendeeTab, attendee]);

  return (
    <Box sx={AttendeeDetailsPageStyle.wrapper}>
   {isTabsVisible && <Tabs value={activeAttendeeTab} sx={AttendeeDetailsPageStyle.tabs}>
        {attendeeDetailsTabs.map((tab: string) => (
          <Tab
            onClick={changeAttendeeTab(tab)}
            key={tab}
            value={tab}
            label={<Box>{tab}</Box>}
            sx={AttendeeDetailsPageStyle.tab}
            disableRipple
          />
        ))}
      </Tabs>}

      {attendeeTabData}
    </Box>
  );
};

export default AttendeeDetailsPage;
